import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, LogOut, Repeat } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { BotonMiCuenta } from "./BotonMiCuenta";
import { AgentOnboardingWidget } from "./AgentOnboardingWidget";
import { BotonBuscadorMenus } from "./BuscadorMenus";
import { WhatsappStatusBadge } from "./WhatsappStatusBadge";
import { NotificacionesAdmin } from "./NotificacionesAdmin";
import { AdminVerComoSelector } from "./AdminVerComoSelector";
import { useAuth } from "@/contexts/AuthContext";
import { CambiarRolDialog } from "@/components/auth/CambiarRolDialog";
import { useRolesAsignados } from "@/hooks/useCambiarRolActivo";
import { APP_VERSION } from "@/lib/config";
import { SozuLogo } from "@/components/ui/sozu-logo";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface AdminHeaderProps {
  onMenuClick: () => void;
}

const SIMPLIFIED_ROLES = ["Agente Inmobiliario", "Inmobiliaria"];

export const AdminHeader = ({ onMenuClick }: AdminHeaderProps) => {
  const { profile, user, signOut } = useAuth();
  const { elegiblesAdmin } = useRolesAsignados();
  const [isCambiarRolOpen, setIsCambiarRolOpen] = useState(false);
  // Multi-rol: solo si hay a qué rol cambiar (más de uno elegible, o el activo
  // no es uno de ellos, p. ej. quedó en Cliente tras entrar por clientes.sozu.com).
  const puedeCambiarRol =
    elegiblesAdmin.length > 1 ||
    (elegiblesAdmin.length === 1 && elegiblesAdmin[0].rol_id !== profile?.rol_id);

  const isSimplifiedRole = SIMPLIFIED_ROLES.includes(profile?.rol_nombre ?? "");

  // Fetch agent commission for simplified roles
  const { data: agentCommission } = useQuery({
    queryKey: ["agent-commission", profile?.id_persona],
    queryFn: async () => {
      if (!profile?.id_persona) return null;
      const { data } = await supabase
        .from("entidades_relacionadas")
        .select("porcentaje_comision")
        .eq("id_persona", profile.id_persona)
        .eq("id_tipo_entidad", 19)
        .eq("activo", true)
        .is("id_proyecto", null)
        .maybeSingle();
      return data?.porcentaje_comision ?? null;
    },
    enabled: isSimplifiedRole && !!profile?.id_persona,
  });

  return (
    <>
      <header className="bg-card border-b px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between">
          {/* Left side */}
          {isSimplifiedRole ? (
            <div className="flex items-center gap-3 shrink-0">
              {/* En móvil el sidebar está colapsado: sin este botón, estos roles
                  se quedaban sin forma de abrir el menú en el panel admin. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onMenuClick}
                className="lg:hidden"
                aria-label="Abrir menú de navegación"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div className="flex flex-col gap-0.5">
                <SozuLogo className="h-7" />
                <span className="text-[9px] text-muted-foreground/40 select-none">{APP_VERSION}</span>
              </div>
              <span className="text-sm text-muted-foreground hidden sm:inline">
                Hola, <span className="font-medium text-foreground">{profile?.nombre?.split(" ")[0] || "Usuario"}</span>
              </span>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMenuClick}
              className="lg:hidden"
              aria-label="Abrir menú de navegación"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {/* Center - Onboarding progress for agents */}
          {isSimplifiedRole && profile?.rol_nombre === "Agente Inmobiliario" && profile?.id_persona && (
            <div className="flex-1 mx-4 max-w-xl hidden sm:block">
              <AgentOnboardingWidget personaId={profile.id_persona} variant="inline" />
            </div>
          )}

          {/* Right side */}
          <div className="flex min-w-0 items-center gap-3 ml-auto">
            {/* "Ver como" (impersonar): solo con el permiso; trae su aviso de solo lectura. */}
            {!isSimplifiedRole && <AdminVerComoSelector className="hidden sm:flex" />}
            <WhatsappStatusBadge />
            <BotonBuscadorMenus />

            {/* User info for simplified roles */}
            {isSimplifiedRole && (
              <div className="flex flex-col items-end mr-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-foreground leading-tight truncate max-w-[120px] sm:max-w-none">
                    {profile?.nombre || "Usuario"}
                  </span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/30 text-primary cursor-pointer hover:bg-primary/5">
                          {agentCommission != null ? `${agentCommission} %` : "2.00 %"}
                        </Badge>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto px-3 py-1.5 text-xs">
                      Porcentaje de comisión
                    </PopoverContent>
                  </Popover>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 hidden sm:inline-flex">
                    {profile?.rol_nombre || "Agente"}
                  </Badge>
                </div>
                <span className="text-[11px] text-muted-foreground leading-tight hidden sm:block">
                  {profile?.email || user?.email}
                </span>
              </div>
            )}

            {!isSimplifiedRole && <ThemeToggle />}

            <NotificacionesAdmin />

            {puedeCambiarRol && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Cambiar de rol"
                    onClick={() => setIsCambiarRolOpen(true)}
                  >
                    <Repeat className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Cambiar de rol</TooltipContent>
              </Tooltip>
            )}

            {!isSimplifiedRole && <BotonMiCuenta />}

            {isSimplifiedRole && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Cerrar sesión"
                onClick={signOut}
                title="Cerrar sesión"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Mobile onboarding - below header content */}
        {isSimplifiedRole && profile?.rol_nombre === "Agente Inmobiliario" && profile?.id_persona && (
          <div className="sm:hidden mt-2">
            <AgentOnboardingWidget personaId={profile.id_persona} variant="inline" />
          </div>
        )}
        {/* "Ver como" en móvil: segunda fila a todo el ancho, igual que en los portales. */}
        {!isSimplifiedRole && (
          <div className="mt-3 sm:hidden">
            <AdminVerComoSelector />
          </div>
        )}
        {puedeCambiarRol && (
          <CambiarRolDialog open={isCambiarRolOpen} onOpenChange={setIsCambiarRolOpen} />
        )}
      </header>
    </>
  );
};
